import React from 'react';
import { Card, CardContent } from './ui/card';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { useScrollAnimation, useStaggeredAnimation } from '../hooks/useScrollAnimation';

const Contact = () => {
  const [titleRef, titleVisible] = useScrollAnimation(0.1);
  const [cardsRef, visibleCards] = useStaggeredAnimation(4, 150);
  const [mapRef, mapVisible] = useScrollAnimation(0.2);

  return (
    <section id="contact" className="py-24 bg-neutral-950">
      <div className="max-w-7xl mx-auto px-6">
        <div 
          ref={titleRef}
          className={`text-center mb-16 fade-in ${titleVisible ? 'visible' : ''}`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Látogass el hozzánk
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Találj meg minket a város szívében
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div ref={cardsRef} className="space-y-6">
            <Card className={`bg-black border-neutral-800 fade-in-left ${visibleCards.has(0) ? 'visible' : ''}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-white/5">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Cím</h3>
                    <p className="text-gray-400">{contactInfo.address}</p>
                    <p className="text-gray-400">{contactInfo.city}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`bg-black border-neutral-800 fade-in-left ${visibleCards.has(1) ? 'visible' : ''}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-white/5">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Telefon</h3>
                    <a
                      href={`tel:${contactInfo.phoneRaw}`}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`bg-black border-neutral-800 fade-in-left ${visibleCards.has(2) ? 'visible' : ''}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-white/5">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email</h3>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`bg-black border-neutral-800 fade-in-left ${visibleCards.has(3) ? 'visible' : ''}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-white/5">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Nyitvatartás</h3>
                    <div className="space-y-1">
                      {contactInfo.hours.map(({ day, time }) => (
                        <p key={day} className="text-gray-400 flex justify-between gap-6 text-sm">
                          <span className="text-white">{day}</span>
                          <span className={time ? '' : 'text-gray-500'}>{time || contactInfo.closedLabel}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Google Maps Placeholder */}
          <div className="h-full min-h-[500px]">
            <Card 
              ref={mapRef}
              className={`bg-black border-neutral-800 h-full overflow-hidden fade-in-right ${mapVisible ? 'visible' : ''}`}
            >
              <CardContent className="p-0 h-full">
                <div className="relative w-full h-full min-h-[500px] rounded-lg overflow-hidden bg-neutral-900">
                  <iframe
                    title="Velvet Cuts — Google Maps"
                    src={`https://www.google.com/maps?q=${contactInfo.mapEmbedQuery}&output=embed`}
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 0, filter: 'invert(0.92) hue-rotate(180deg) contrast(0.95) saturate(0.85)' }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${contactInfo.mapEmbedQuery}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-4 right-4 z-10 bg-black/80 backdrop-blur-sm border border-white/20 hover:border-white text-white text-xs uppercase tracking-wider font-montserrat font-medium px-4 py-2.5 rounded-sm transition-all"
                  >
                    Útvonal →
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
